import { LocalizedText, isLocalizedText } from "../interfaces/localized"
import { IntlKey, isIntlKey } from "../intl"

type TranslationEntryData = Record<string, string | number>

/**
 * A plain object representation of a instance used in the JSON serialization.
 * The properties are very explicit to avoid possible ambiguity with other
 * JSON objects.
 */
export interface SerializedTranslationEntry {
  translationKey: string | LocalizedText
  translationData?: TranslationEntryData
}

/**
 * Represents a translation entry - a translation key and possibly data
 * needed for the translation key. This allows passing full information
 * about translatable content across the codebase, simplifying many workflows
 * (notably, sending translation keys backend to frontend).
 *
 * More details are described in [RFC 0019](https://buurtdokters.atlassian.net/wiki/spaces/IT/pages/95579650392069/)
 */
export class TranslationEntry {
  constructor(
    public readonly key: IntlKey | LocalizedText,
    public readonly data?: TranslationEntryData
  ) {}

  /**
   * Reconstructs an instance from a plain object produced by
   * .toJSON() method.
   *
   * @throws {Error} if the key is not a valid IntlKey.
   */
  static fromJSON(json: SerializedTranslationEntry): TranslationEntry {
    if (isIntlKey(json.translationKey)) {
      return new TranslationEntry(json.translationKey, json.translationData)
    }
    if (isLocalizedText(json.translationKey)) {
      return new TranslationEntry(json.translationKey, json.translationData)
    }
    throw new Error(`Invalid IntlKey: "${JSON.stringify(json.translationKey)}"`)
  }

  /**
   * Create a TranslationEntry from a LocalizedText. This usage will essentially
   * skip the i18n registry that we have for translations, and use the LocalizedText
   * directly.
   */
  static fromLocalizedText(localizedText: LocalizedText, data?: TranslationEntryData): TranslationEntry {
    return new TranslationEntry(localizedText, data)
  }

  /**
   * Static factory that can be passed around as a callback, e.g.
   * `items.map(TranslationEntry.create)`.
   */
  static create(key: IntlKey, data?: TranslationEntryData): TranslationEntry {
    return new TranslationEntry(key, data)
  }

  /**
   * Converts an instance to a JSON-serializable representation.
   */
  toJSON(): SerializedTranslationEntry {
    const result: SerializedTranslationEntry = { translationKey: this.key }
    if (this.data !== undefined) {
      result.translationData = this.data
    }
    return result
  }
}
